/**
 * ResponsiveShell — switches between desktop TopNav and mobile MobileLayout
 * based on screen width. Routes are unchanged; only the navigation chrome differs.
 */

import { ReactNode, useState, useCallback } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { usePlatformContext } from '@/platform'
import { buildLocalizedPath } from '@/i18n'
import { TopNav } from '@/modules/shared/components/Layout/TopNav'
import { MobileHeader } from '@/layouts/MobileHeader'
import { MobileBottomNav } from '@/layouts/MobileBottomNav'
import { MobileDrawer } from '@/layouts/MobileDrawer'
import { AmbientPageBackdrop } from '@/modules/shared/components/Layout/AmbientPageBackdrop'
import { PWAInstallBanner } from '@/modules/shared/components/PWAInstallBanner'
import { SWUpdateToast } from '@/modules/shared/components/SWUpdateToast'
import { ToastContainer } from '@/modules/shared/components/Toast'
import { useAuth } from '@/modules/shared/contexts/AuthContext'

interface ResponsiveShellProps {
  children: ReactNode
}

/** Map bottom nav tab IDs to route paths */
const TAB_ROUTES: Record<string, string> = {
  feed: '/feed',
  discover: '/agent/plaza',
  myagent: '/agent/me',
  circles: '/circles',
  alerts: '/notifications',
}

/** Tabs that require authentication */
const AUTH_TABS = new Set(['myagent', 'alerts', 'messages'])

/** Drawer items that require login */
const AUTH_DRAWER_ITEMS = new Set(['profile', 'myagent', 'messages', 'bookmarks', 'membership'])

/** Map drawer menu item IDs to route paths */
const DRAWER_ROUTES: Record<string, string> = {
  profile: '/user/__USER_ID__',
  myagent: '/agent/me',
  messages: '/messages',
  circles: '/circles',
  membership: '/membership',
  settings: '/settings',
  help: '/help',
}

/** Determine active bottom nav tab from current URL path */
function getActiveTab(pathname: string): string {
  const path = pathname.replace(/^\/(en|zh-CN|ja)/, '')
  if (path.startsWith('/notifications')) return 'alerts'
  if (path.startsWith('/circles')) return 'circles'
  if (path.startsWith('/agent/me')) return 'myagent'
  if (path.startsWith('/agent')) return 'discover'
  if (path.startsWith('/feed') || path === '' || path === '/') return 'feed'
  return ''
}

/** Is this a detail page that should show a back button instead of hamburger? */
function isDetailPage(pathname: string): boolean {
  const path = pathname.replace(/^\/(en|zh-CN|ja)/, '')
  return /^\/(post|articles|user|topic|circles|activities|agent)\/[^/]+/.test(path)
    && !path.startsWith('/agent/me')
    && !path.startsWith('/agent/plaza')
    && !path.startsWith('/agent/discovery')
    && !path.startsWith('/agent/home')
}

/** Pages where bottom nav should be hidden (full-screen experiences) */
function shouldHideBottomNav(pathname: string): boolean {
  const path = pathname.replace(/^\/(en|zh-CN|ja)/, '')
  return /\/agent\/[^/]+\/chat$/.test(path)
    || /\/messages\/[^/]+$/.test(path)
}

function shouldUseFullscreenMobilePage(pathname: string): boolean {
  const path = pathname.replace(/^\/(en|zh-CN|ja)/, '')
  return /\/agent\/[^/]+\/chat$/.test(path)
}

/** Desktop TopNav is fixed; content-led pages need room below it. */
function shouldOffsetDesktopTopNav(pathname: string): boolean {
  const path = pathname.replace(/^\/(en|zh-CN|ja)/, '')
  return [
    /^\/feed$/,
    /^\/messages(?:\/|$)/,
    /^\/notifications$/,
    /^\/search$/,
    /^\/user(?:\/|$)/,
    /^\/topic(?:\/|$)/,
    /^\/post(?:\/|$)/,
    /^\/articles(?:\/|$)/,
    /^\/circles(?:\/|$)/,
    /^\/activities(?:\/|$)/,
    /^\/membership$/,
    /^\/account$/,
    /^\/settings$/,
    /^\/help(?:\/|$)/,
    /^\/privacy$/,
    /^\/terms$/,
    /^\/child-safety$/,
    /^\/suspended-accounts$/,
    /^\/disclaimer$/,
    /^\/me(?:\/|$)/,
    /^\/agent\/[^/]+\/chat$/,
    /^\/studio(?:\/|$)/,
    /^\/admin\/subscription(?:\/|$)/,
    /^\/brand(?:\/|$)/,
  ].some((pattern) => pattern.test(path))
}

function shouldUseAmbientBackdrop(pathname: string): boolean {
  const path = pathname.replace(/^\/(en|zh-CN|ja)/, '')
  return [
    /^\/feed$/,
    /^\/post(?:\/|$)/,
    /^\/articles(?:\/|$)/,
    /^\/topic(?:\/|$)/,
    /^\/agent\/[^/]+\/chat$/,
    /^\/circles(?:\/|$)/,
    /^\/me\/circles$/,
    /^\/me\/subscriptions$/,
    /^\/activities(?:\/|$)/,
    /^\/membership$/,
    /^\/help(?:\/|$)/,
    /^\/privacy$/,
    /^\/terms$/,
    /^\/child-safety$/,
    /^\/suspended-accounts$/,
    /^\/disclaimer$/,
  ].some((pattern) => pattern.test(path))
}

export function ResponsiveShell({ children }: ResponsiveShellProps) {
  const { isMobile, safeAreaInsets } = usePlatformContext()
  const location = useLocation()
  const navigate = useNavigate()
  const { lang } = useParams<{ lang: string }>()
  const { user } = useAuth()
  const [drawerOpen, setDrawerOpen] = useState(false)

  const currentLang = lang || 'en'
  const activeTab = getActiveTab(location.pathname)
  const showBack = isDetailPage(location.pathname)
  const hideBottomNav = shouldHideBottomNav(location.pathname)
  const fullscreenMobilePage = shouldUseFullscreenMobilePage(location.pathname)
  const offsetDesktopTopNav = shouldOffsetDesktopTopNav(location.pathname)
  const useAmbientBackdrop = shouldUseAmbientBackdrop(location.pathname)
  const wantsImageOnlyBackdrop = /^\/(en|zh-CN|ja)?\/?(circles|me\/circles|membership)(\/|$)/.test(location.pathname)
  const ambientVariant: 'default' | 'imageOnly' = wantsImageOnlyBackdrop ? 'imageOnly' : 'default'

  const handleTabNavigate = useCallback((tabId: string) => {
    const route = TAB_ROUTES[tabId]
    if (!route) return

    if (AUTH_TABS.has(tabId) && !user) {
      window.dispatchEvent(new CustomEvent('open-login-modal'))
      return
    }

    navigate(buildLocalizedPath(currentLang as 'en' | 'zh-CN' | 'ja', route))
  }, [navigate, currentLang, user])

  const handleDrawerNavigate = useCallback((itemId: string) => {
    setDrawerOpen(false)

    if (itemId === 'login') {
      window.dispatchEvent(new CustomEvent('open-login-modal'))
      return
    }

    // Block auth-required items for non-logged-in users
    if (AUTH_DRAWER_ITEMS.has(itemId) && !user) {
      window.dispatchEvent(new CustomEvent('open-login-modal'))
      return
    }

    let route = DRAWER_ROUTES[itemId]
    if (!route) return

    if (itemId === 'profile' && user) {
      route = route.replace('__USER_ID__', user.id)
    }

    navigate(buildLocalizedPath(currentLang as 'en' | 'zh-CN' | 'ja', route))
  }, [navigate, currentLang, user])

  const handleBack = useCallback(() => {
    navigate(-1)
  }, [navigate])

  // Desktop: render TopNav only, children pass through
  if (!isMobile) {
    return (
      <>
        <TopNav />
        {useAmbientBackdrop && <AmbientPageBackdrop variant={ambientVariant} />}
        <main
          className={[
            offsetDesktopTopNav ? 'pt-16' : '',
            useAmbientBackdrop ? 'relative z-10 min-h-screen' : '',
          ].filter(Boolean).join(' ') || undefined}
        >
          {children}
        </main>
      </>
    )
  }

  // Mobile: render MobileLayout shell
  return (
    <div className={fullscreenMobilePage ? 'flex h-[100dvh] flex-col bg-[var(--y-bg)]' : 'min-h-screen flex flex-col bg-[var(--y-bg)]'}>
      {/* Top safe area — uses CSS env() as primary, JS value as fallback */}
      <div
        className="bg-[var(--y-bg)] shrink-0"
        style={{ height: `max(${safeAreaInsets.top}px, env(safe-area-inset-top, 0px))` }}
      />

      {!fullscreenMobilePage && (
        <MobileHeader
          showBack={showBack}
          onBack={handleBack}
          onMenuClick={() => setDrawerOpen(true)}
        />
      )}

      <main
        className={fullscreenMobilePage ? 'min-h-0 flex-1 overflow-hidden' : 'flex-1 overflow-y-auto overflow-x-hidden'}
        style={{
          paddingBottom: fullscreenMobilePage
            ? 0
            : hideBottomNav
            ? `max(${safeAreaInsets.bottom}px, env(safe-area-inset-bottom, 0px))`
            : `calc(56px + max(${safeAreaInsets.bottom}px, env(safe-area-inset-bottom, 0px)))`,
        }}
      >
        {children}
      </main>

      {!hideBottomNav && (
        <MobileBottomNav
          activeItem={activeTab}
          onNavigate={handleTabNavigate}
          safeAreaBottom={safeAreaInsets.bottom}
        />
      )}

      <MobileDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onNavigate={handleDrawerNavigate}
      />

      {!hideBottomNav && <PWAInstallBanner />}
      <SWUpdateToast />
      <ToastContainer />
    </div>
  )
}
